/**
 * Hidden reference page /design (D-104): every v2 token and component, in the current theme and
 * side by side in Crystal and Midnight. The team rebuilding each surface copies from here.
 * Texts: i18n namespace « design ».
 */

import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  GlassCard,
  GlassSheet,
  IconButton,
  Kbd,
  Menu,
  PageHeader,
  SegmentedControl,
  SelectField,
  Skeleton,
  SkeletonText,
  Spinner,
  Switch,
  Tabs,
  TextAreaField,
  TextField,
  Tooltip,
  tabPanelProps,
  useToast,
} from "@agencyos/ui-web";
import {
  Archive,
  Check,
  Copy,
  EllipsisVertical,
  FileCheck2,
  Info,
  MessageCircle,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  UsersRound,
} from "lucide-react";
import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import "./design.css";

const COLOR_GROUPS: { key: string; tokens: string[] }[] = [
  { key: "backgrounds", tokens: ["bg", "bg-elevated", "bg-grouped", "bg-tertiary"] },
  { key: "labels", tokens: ["label", "label-secondary", "label-tertiary", "label-quaternary"] },
  { key: "fills", tokens: ["fill", "fill-secondary", "fill-tertiary", "fill-quaternary", "separator", "border"] },
  { key: "accent", tokens: ["accent", "accent-fill", "accent-fill-hover", "accent-fill-pressed", "accent-tint", "focus-ring"] },
  { key: "status", tokens: ["success", "success-tint", "warning", "warning-tint", "danger", "danger-tint", "info", "info-tint"] },
];

const MATERIALS = ["ultraThin", "thin", "regular", "thick"] as const;
const MATERIAL_CLASS: Record<(typeof MATERIALS)[number], string> = {
  ultraThin: "ao-glass ao-glass--ultra-thin",
  thin: "ao-glass ao-glass--thin",
  regular: "ao-glass ao-glass--blur",
  thick: "ao-glass ao-glass--thick",
};

const TYPE_ROLES = [
  ["largeTitle", "ao-large-title", "30 / 36 · 700"],
  ["title1", "ao-title-1", "24 / 30 · 700"],
  ["title2", "ao-title-2", "19 / 25 · 600"],
  ["title3", "ao-title-3", "16 / 22 · 600"],
  ["headline", "ao-headline", "14 / 20 · 600"],
  ["body", "ao-body", "14 / 21 · 400"],
  ["callout", "ao-callout", "13 / 19 · 400"],
  ["subheadline", "ao-subheadline ao-secondary", "13 / 18 · 500"],
  ["footnote", "ao-footnote ao-secondary", "12 / 16 · 400"],
  ["caption", "ao-caption ao-secondary", "11 / 14 · 500"],
] as const;

const ICON = { size: 16, strokeWidth: 1.75, "aria-hidden": true } as const;

function Section({ id, title, intro, children }: { id: string; title: string; intro?: string; children: ReactNode }) {
  return (
    <section className="design-section" aria-labelledby={`design-${id}`}>
      <div className="design-section__head">
        <h2 id={`design-${id}`} className="ao-title-2">
          {title}
        </h2>
        {intro ? <p className="ao-callout ao-secondary">{intro}</p> : null}
      </div>
      {children}
    </section>
  );
}

function Swatch({ token }: { token: string }) {
  return (
    <li className="design-swatch">
      <span className="design-swatch__chip" style={{ background: `var(--ao-${token})` } as CSSProperties} aria-hidden="true" />
      <code>--ao-{token}</code>
    </li>
  );
}

/** A compact composition used for the side-by-side theme preview. */
function Composition() {
  const { t } = useTranslation();
  return (
    <div className="design-compose">
      <GlassCard className="stack">
        <div className="ao-card__header">
          <h3 className="ao-card__title">{t("design.card.listTitle")}</h3>
          <Badge tone="accent">4</Badge>
        </div>
        <table className="ao-table">
          <tbody>
            <tr>
              <td className="ao-num">
                <strong>09:00</strong>
              </td>
              <td>Awa Ngono</td>
              <td>
                <Badge tone="success" dot>
                  {t("design.badge.success")}
                </Badge>
              </td>
            </tr>
            <tr>
              <td className="ao-num">
                <strong>10:30</strong>
              </td>
              <td>Jean-Paul Mbarga</td>
              <td>
                <Badge tone="warning" dot>
                  {t("design.badge.warning")}
                </Badge>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="row">
          <Button>{t("design.button.primary")}</Button>
          <Button variant="secondary">{t("design.button.secondary")}</Button>
          <Button variant="tinted">{t("design.button.tinted")}</Button>
        </div>
      </GlassCard>
    </div>
  );
}

export function DesignPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [opaque, setOpaque] = useState(false);
  const [period, setPeriod] = useState<"day" | "week" | "month">("week");
  const [notify, setNotify] = useState(true);
  const [tab, setTab] = useState<"profile" | "documents" | "payments">("profile");
  const [sheet, setSheet] = useState(false);
  const [dialog, setDialog] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("ao-no-glass", opaque);
    return () => document.documentElement.classList.remove("ao-no-glass");
  }, [opaque]);

  return (
    <main className="app-main design-page">
      <PageHeader
        eyebrow={t("design.page.eyebrow")}
        title={t("design.page.title")}
        subtitle={t("design.page.subtitle")}
        actions={
          <SegmentedControl<"glass" | "opaque">
            label={t("design.page.glassMode")}
            value={opaque ? "opaque" : "glass"}
            options={[
              { value: "glass", label: t("design.page.glassOn") },
              { value: "opaque", label: t("design.page.glassOff") },
            ]}
            onChange={(value) => setOpaque(value === "opaque")}
          />
        }
      />

      <Section id="themes" title={t("design.sections.themes")}>
        <div className="design-themes">
          <div data-theme="light" className="design-theme-pane">
            <p className="ao-caption ao-secondary">{t("settings.themeLight")}</p>
            <Composition />
          </div>
          <div data-theme="dark" className="design-theme-pane">
            <p className="ao-caption ao-secondary">{t("settings.themeDark")}</p>
            <Composition />
          </div>
        </div>
      </Section>

      <Section id="colors" title={t("design.sections.colors")} intro={t("design.sections.colorsIntro")}>
        <div className="design-colors">
          {COLOR_GROUPS.map((group) => (
            <GlassCard key={group.key} className="stack">
              <h3 className="ao-headline">{t(`design.color.${group.key}`)}</h3>
              <ul className="design-swatches">
                {group.tokens.map((token) => (
                  <Swatch key={token} token={token} />
                ))}
              </ul>
            </GlassCard>
          ))}
        </div>
      </Section>

      <Section id="materials" title={t("design.sections.materials")} intro={t("design.sections.materialsIntro")}>
        <div className="design-materials">
          <div className="design-materials__art" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          {MATERIALS.map((material) => (
            <div key={material} className={`${MATERIAL_CLASS[material]} design-material`}>
              <strong className="ao-headline">{t(`design.material.${material}`)}</strong>
              <span className="ao-footnote ao-secondary">{t(`design.material.${material}Use`)}</span>
              <code className="ao-caption">--ao-glass-{material.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}-bg</code>
            </div>
          ))}
        </div>
      </Section>

      <Section id="type" title={t("design.sections.type")} intro={t("design.sections.typeIntro")}>
        <GlassCard className="design-type">
          {TYPE_ROLES.map(([role, className, spec]) => (
            <div key={role} className="design-type__row">
              <span className="ao-caption ao-secondary design-type__meta">
                {role}
                <br />
                {spec}
              </span>
              <span className={className}>{t(`design.typeSample.${role}`)}</span>
            </div>
          ))}
          <div className="design-type__row">
            <span className="ao-caption ao-secondary design-type__meta">
              tabular-nums
              <br />
              .ao-num
            </span>
            <span className="ao-title-2 ao-num">{t("design.typeSample.amount")}</span>
          </div>
        </GlassCard>
      </Section>

      <Section id="buttons" title={t("design.sections.buttons")} intro={t("design.sections.buttonsIntro")}>
        <GlassCard className="stack">
          <div className="row">
            <Button>{t("design.button.primary")}</Button>
            <Button variant="secondary">{t("design.button.secondary")}</Button>
            <Button variant="tinted" icon={<Plus {...ICON} />}>
              {t("design.button.tinted")}
            </Button>
            <Button variant="ghost">{t("design.button.ghost")}</Button>
            <Button variant="plain" icon={<SlidersHorizontal {...ICON} />}>
              {t("design.button.plain")}
            </Button>
            <Button variant="destructive" icon={<Trash2 {...ICON} />}>
              {t("design.button.destructive")}
            </Button>
            <Button variant="whatsapp" icon={<MessageCircle {...ICON} />}>
              {t("design.button.whatsapp")}
            </Button>
          </div>
          <p className="ao-subheadline ao-secondary">{t("design.button.states")}</p>
          <div className="row">
            <Button loading>{t("design.button.loading")}</Button>
            <Button variant="secondary" loading>
              {t("design.button.loading")}
            </Button>
            <Button disabled>{t("design.button.disabled")}</Button>
            <Button variant="secondary" disabled>
              {t("design.button.disabled")}
            </Button>
          </div>
          <p className="ao-subheadline ao-secondary">{t("design.button.sizes")}</p>
          <div className="row">
            <Button size="sm">{t("design.button.small")}</Button>
            <Button size="sm" variant="secondary">
              {t("design.button.small")}
            </Button>
            <Button size="lg">{t("design.button.large")}</Button>
            <IconButton label={t("design.button.icon")}>
              <EllipsisVertical {...ICON} />
            </IconButton>
            <IconButton label={t("design.button.icon")} variant="secondary">
              <SlidersHorizontal {...ICON} />
            </IconButton>
          </div>
        </GlassCard>
      </Section>

      <Section id="fields" title={t("design.sections.fields")}>
        <GlassCard className="design-fields">
          <TextField label={t("design.field.name")} placeholder={t("design.field.namePlaceholder")} />
          <TextField label={t("design.field.phone")} hint={t("design.field.phoneHint")} defaultValue="+237 6 77 11 22 33" />
          <TextField label={t("design.field.email")} error={t("design.field.emailError")} defaultValue="awa@" />
          <SelectField
            label={t("design.field.service")}
            options={[
              { value: "rp", label: t("design.field.serviceRp") },
              { value: "visa", label: t("design.field.serviceVisa") },
              { value: "ru", label: t("design.field.serviceRussia") },
            ]}
          />
          <div className="ao-field">
            <label className="ao-field__label" htmlFor="design-search">
              {t("design.field.search")}
            </label>
            <div className="ao-input-icon">
              <Search size={15} strokeWidth={2} aria-hidden="true" />
              <input id="design-search" type="search" className="ao-field__input" placeholder={t("design.shell.searchPlaceholder")} />
            </div>
          </div>
          <TextAreaField label={t("design.field.notes")} placeholder={t("design.field.notesPlaceholder")} rows={3} />
          <div className="stack">
            <span className="ao-field__label">{t("design.field.period")}</span>
            <SegmentedControl<"day" | "week" | "month">
              label={t("design.field.period")}
              value={period}
              options={[
                { value: "day", label: t("design.field.day") },
                { value: "week", label: t("design.field.week") },
                { value: "month", label: t("design.field.month") },
              ]}
              onChange={(value) => setPeriod(value)}
            />
          </div>
          <div className="stack">
            <Switch label={t("design.field.notify")} checked={notify} onChange={setNotify} />
            <Checkbox label={t("design.field.consent")} hint={t("design.field.consentHint")} defaultChecked />
            <div className="row" role="radiogroup" aria-label={t("design.field.period")}>
              <Checkbox kind="radio" name="design-mode" label={t("design.field.office")} defaultChecked />
              <Checkbox kind="radio" name="design-mode" label={t("design.field.call")} />
            </div>
          </div>
        </GlassCard>
      </Section>

      <Section id="cards" title={t("design.sections.cards")}>
        <div className="design-cards">
          <GlassCard as="article" className="kpi">
            <div className="kpi__link">
              <span className="kpi__label">
                <FileCheck2 {...ICON} /> {t("design.card.kpiLabel")}
              </span>
              <span className="kpi__value">6</span>
              <span className="kpi__hint">{t("design.card.kpiHint")}</span>
            </div>
          </GlassCard>
          <GlassCard className="stack">
            <div>
              <h3 className="ao-card__title">{t("design.card.listTitle")}</h3>
              <p className="ao-card__subtitle">{t("design.card.listSubtitle")}</p>
            </div>
            <div className="row">
              <Avatar name="Awa Ngono" />
              <Avatar name="Jean-Paul Mbarga" />
              <Avatar name="Fadimatou Bello" />
            </div>
          </GlassCard>
          <GlassCard interactive className="stack">
            <h3 className="ao-card__title">{t("design.card.interactive")}</h3>
            <p className="ao-callout ao-secondary">{t("design.card.interactiveHint")}</p>
          </GlassCard>
          <div className="ao-surface ao-card stack">
            <h3 className="ao-card__title">{t("design.card.surface")}</h3>
            <p className="ao-callout ao-secondary">{t("design.card.surfaceHint")}</p>
          </div>
        </div>
      </Section>

      <Section id="table" title={t("design.sections.table")}>
        <GlassCard>
          <div className="ao-table-wrap">
            <table className="ao-table">
              <thead>
                <tr>
                  <th scope="col">{t("design.tableDemo.client")}</th>
                  <th scope="col">{t("design.tableDemo.reference")}</th>
                  <th scope="col">{t("design.tableDemo.status")}</th>
                  <th scope="col" className="num">
                    {t("design.tableDemo.amount")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Awa Ngono", "FAC-2026-0112", "success", "paid", "1 250 000 FCFA"],
                  ["Jean-Paul Mbarga", "FAC-2026-0113", "warning", "pending", "350 000 FCFA"],
                  ["Fadimatou Bello", "FAC-2026-0114", "danger", "late", "75 000 FCFA"],
                  ["Christelle Essomba", "FAC-2026-0115", "default", "draft", "2 400 000 FCFA"],
                ].map(([name, ref, tone, status, amount]) => (
                  <tr key={ref}>
                    <td>
                      <span className="design-person">
                        <Avatar name={name ?? ""} />
                        {name}
                      </span>
                    </td>
                    <td className="ao-secondary ao-num">{ref}</td>
                    <td>
                      <Badge tone={tone as "success" | "warning" | "danger" | "default"} dot>
                        {t(`design.tableDemo.${status}`)}
                      </Badge>
                    </td>
                    <td className="num ao-num">{amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      </Section>

      <Section id="badges" title={t("design.sections.badges")}>
        <GlassCard className="stack">
          <div className="row">
            <Badge>{t("design.badge.neutral")}</Badge>
            <Badge tone="accent">{t("design.badge.accent")}</Badge>
            <Badge tone="success" dot>
              {t("design.badge.success")}
            </Badge>
            <Badge tone="warning" dot>
              {t("design.badge.warning")}
            </Badge>
            <Badge tone="danger" dot>
              {t("design.badge.danger")}
            </Badge>
            <Badge tone="info">{t("design.badge.info")}</Badge>
            <Badge tone="offer">{t("design.badge.offer")}</Badge>
            <span className="ao-count">3</span>
          </div>
          <Tabs
            label={t("design.badge.tabsLabel")}
            idPrefix="design-tabs"
            value={tab}
            onChange={setTab}
            items={[
              { value: "profile", label: t("design.badge.tabProfile") },
              { value: "documents", label: t("design.badge.tabDocuments"), count: 12 },
              { value: "payments", label: t("design.badge.tabPayments") },
            ]}
          />
          <p className="ao-callout ao-secondary" {...tabPanelProps("design-tabs", tab)}>
            {t(`design.badge.panel${tab.charAt(0).toUpperCase()}${tab.slice(1)}`)}
          </p>
        </GlassCard>
      </Section>

      <Section id="overlays" title={t("design.sections.overlays")}>
        <GlassCard className="stack">
          <div className="row">
            <Button variant="secondary" onClick={() => setSheet(true)}>
              {t("design.overlay.openSheet")}
            </Button>
            <Button variant="secondary" onClick={() => setDialog(true)}>
              {t("design.overlay.openDialog")}
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                toast.show(t("design.overlay.toast"), {
                  tone: "success",
                  icon: <Check size={16} strokeWidth={2} />,
                  action: { label: t("design.overlay.undo"), onClick: () => undefined },
                })
              }
            >
              {t("design.overlay.showToast")}
            </Button>
            <Menu
              label={t("design.overlay.menu")}
              trigger={
                <>
                  <EllipsisVertical {...ICON} />
                  <span>{t("design.overlay.menuButton")}</span>
                </>
              }
              triggerClassName="ao-button ao-button--secondary"
              items={[
                { label: t("design.overlay.menuEdit"), icon: <Pencil {...ICON} />, shortcut: "E", onSelect: () => undefined },
                { label: t("design.overlay.menuDuplicate"), icon: <Copy {...ICON} />, shortcut: "D", onSelect: () => undefined },
                { label: t("design.overlay.menuArchive"), icon: <Archive {...ICON} />, onSelect: () => undefined },
                { separator: true },
                { label: t("design.overlay.menuDelete"), icon: <Trash2 {...ICON} />, danger: true, onSelect: () => undefined },
              ]}
            />
            <Tooltip content={t("design.overlay.tooltipText")}>
              <button type="button" className="ao-button ao-button--plain">
                <Info {...ICON} />
                {t("design.overlay.tooltip")}
              </button>
            </Tooltip>
          </div>
          <p className="ao-footnote ao-secondary">
            {t("design.overlay.palette")} <Kbd>⌘</Kbd> <Kbd>K</Kbd>
          </p>
        </GlassCard>
        <GlassSheet open={sheet} title={t("design.overlay.sheetTitle")} onClose={() => setSheet(false)}>
          <div className="stack">
            <p className="ao-secondary">{t("design.overlay.sheetBody")}</p>
            <TextField label={t("design.field.name")} placeholder={t("design.field.namePlaceholder")} />
            <TextField label={t("design.field.phone")} hint={t("design.field.phoneHint")} />
            <div className="ao-sheet__actions">
              <Button variant="secondary" onClick={() => setSheet(false)}>
                {t("design.button.secondary")}
              </Button>
              <Button onClick={() => setSheet(false)}>{t("design.button.primary")}</Button>
            </div>
          </div>
        </GlassSheet>
        <Dialog
          open={dialog}
          role="alertdialog"
          title={t("design.overlay.dialogTitle")}
          onClose={() => setDialog(false)}
          actions={
            <>
              <Button variant="secondary" onClick={() => setDialog(false)}>
                {t("design.button.secondary")}
              </Button>
              <Button variant="destructive" onClick={() => setDialog(false)}>
                {t("design.button.destructive")}
              </Button>
            </>
          }
        >
          {t("design.overlay.dialogBody")}
        </Dialog>
      </Section>

      <Section id="loading" title={t("design.sections.loading")}>
        <div className="design-cards">
          <GlassCard className="stack">
            <div className="row">
              <Skeleton className="ao-skeleton--circle" style={{ width: 36, height: 36 }} />
              <div style={{ flex: 1 }}>
                <SkeletonText lines={2} label={t("design.empty.loading")} />
              </div>
            </div>
            <SkeletonText lines={3} />
            <Spinner label={t("design.empty.loading")} />
          </GlassCard>
          <GlassCard>
            <EmptyState
              icon={<UsersRound size={22} strokeWidth={1.75} />}
              title={t("design.empty.title")}
              description={t("design.empty.description")}
              actions={<Button icon={<Plus {...ICON} />}>{t("design.empty.action")}</Button>}
            />
          </GlassCard>
        </div>
      </Section>

      <Section id="foundations" title={t("design.sections.foundations")}>
        <div className="design-cards">
          <GlassCard className="stack">
            <h3 className="ao-headline">{t("design.foundation.space")}</h3>
            <div className="design-spaces">
              {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((step) => (
                <span key={step} className="design-space">
                  <span style={{ width: `var(--ao-space-${step})`, height: `var(--ao-space-${step})` }} />
                  <code className="ao-caption">{step * 4}</code>
                </span>
              ))}
            </div>
          </GlassCard>
          <GlassCard className="stack">
            <h3 className="ao-headline">{t("design.foundation.radius")}</h3>
            <div className="design-radii">
              {["sm", "md", "lg", "xl", "xxl"].map((r) => (
                <span key={r} className="design-radius" style={{ borderRadius: `var(--ao-radius-${r})` }}>
                  <code className="ao-caption">{r}</code>
                </span>
              ))}
            </div>
          </GlassCard>
          <GlassCard className="stack">
            <h3 className="ao-headline">{t("design.foundation.shadow")}</h3>
            <div className="design-radii">
              {["xs", "sm", "md", "lg", "xl"].map((s) => (
                <span key={s} className="design-shadow" style={{ boxShadow: `var(--ao-shadow-${s})` }}>
                  <code className="ao-caption">{s}</code>
                </span>
              ))}
            </div>
          </GlassCard>
        </div>
      </Section>
    </main>
  );
}
